/**
 * translation slice: 자막/번역 설정 상태 관리
 *
 * 기획서 기반:
 * - 자막 표시: 음성인식을 설정한 참여자들의 발언을 자막으로 제공
 * - 번역 자막: 발언 내용을 원하는 언어로 실시간 번역하여 자막으로 제공
 * - 번역 검증: 내가 발언한 내용이 다른 참여자의 언어로 번역된 결과를 확인 (다음 배포)
 * - ONE AI 배포자만 자막/번역 진입점 노출
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  TranslationLanguage,
  FontSizeOption,
  SystemMessageType,
  SystemMessage,
  TranslatingUser,
} from '@/types/translation';
import {
  CREDIT_THRESHOLD,
  TRANSLATION_LANGUAGES,
  FONT_SIZE_OPTIONS,
  LANGUAGE_FLAG_MAP,
  LANGUAGE_LOCALE_MAP,
} from '@/types/translation';

// re-export for backwards compatibility
export type {
  TranslationLanguage,
  FontSizeOption,
  SystemMessageType,
  SystemMessage,
  TranslatingUser,
} from '@/types/translation';
export {
  CREDIT_THRESHOLD,
  TRANSLATION_LANGUAGES,
  FONT_SIZE_OPTIONS,
  LANGUAGE_FLAG_MAP,
  LANGUAGE_LOCALE_MAP,
} from '@/types/translation';

export interface TranslationState {
  /** ONE AI 배포 여부 — 서버 API 확인 후 설정 */
  isAbleOneAI: boolean;
  /** 자막 표시 ON/OFF (기획서: 자막표시 기본 OFF) */
  isCaptionEnabled: boolean;
  /** 번역 자막 ON/OFF */
  isTranslateEnabled: boolean;
  /** 자동언어인식 ON/OFF */
  isAutoLangEnabled: boolean;
  /** 번역 검증 ON/OFF (다음 배포, 항상 false) */
  isVerifyEnabled: boolean;
  /** 자막/번역 언어 */
  language: TranslationLanguage;
  /** 자막 글꼴 크기 (%) */
  fontSize: FontSizeOption;
  /** 크레딧 잔여 여부 — 크레딧 조회 API 응답으로 갱신 */
  creditAvailable: boolean;
  /** 잔여 크레딧 — 크레딧 조회 API 응답의 total_credit */
  totalCredit: number;
  /** 사이드바: 설정 패널(true) vs 히스토리 패널(false) */
  isSettingMode: boolean;
  /** 시스템 메시지 목록 ("실시간 자막이 시작되었습니다" 등) */
  systemMessages: SystemMessage[];
  /** 번역 활성 참여자 맵: userId → { enabled, language } */
  translatingUsers: Record<string, TranslatingUser>;
}

const initialState: TranslationState = {
  isAbleOneAI: false,
  isCaptionEnabled: false,
  isTranslateEnabled: false,
  isAutoLangEnabled: false,
  isVerifyEnabled: false,
  language: 'ko',
  fontSize: 100,
  creditAvailable: true,
  totalCredit: 0,
  isSettingMode: true,
  systemMessages: [],
  translatingUsers: {},
};

let systemMsgCounter = 0;

const translationSlice = createSlice({
  name: 'translation',
  initialState,
  reducers: {
    setIsAbleOneAI(state, action: PayloadAction<boolean>) {
      state.isAbleOneAI = action.payload;
    },

    setCaptionEnabled(state, action: PayloadAction<boolean>) {
      state.isCaptionEnabled = action.payload;
    },

    setTranslateEnabled(state, action: PayloadAction<boolean>) {
      state.isTranslateEnabled = action.payload;
    },

    setAutoLangEnabled(state, action: PayloadAction<boolean>) {
      state.isAutoLangEnabled = action.payload;
    },

    setVerifyEnabled(state, action: PayloadAction<boolean>) {
      state.isVerifyEnabled = action.payload;
    },

    setLanguage(state, action: PayloadAction<TranslationLanguage>) {
      state.language = action.payload;
    },

    setFontSize(state, action: PayloadAction<FontSizeOption>) {
      state.fontSize = action.payload;
    },

    setCreditAvailable(state, action: PayloadAction<boolean>) {
      state.creditAvailable = action.payload;
    },

    setTotalCredit(state, action: PayloadAction<number>) {
      state.totalCredit = action.payload;
      state.creditAvailable = action.payload >= CREDIT_THRESHOLD;
    },

    deductCredit(state, action: PayloadAction<number>) {
      state.totalCredit = Math.max(0, state.totalCredit - action.payload);
      state.creditAvailable = state.totalCredit >= CREDIT_THRESHOLD;
    },

    setUserTranslationStatus(state, action: PayloadAction<{
      userId: string;
      enabled: boolean;
      language: TranslationLanguage;
    }>) {
      const { userId, enabled, language } = action.payload;
      if (enabled) {
        state.translatingUsers[userId] = { enabled, language };
      } else {
        delete state.translatingUsers[userId];
      }
    },

    removeTranslatingUser(state, action: PayloadAction<string>) {
      delete state.translatingUsers[action.payload];
    },

    setSettingMode(state, action: PayloadAction<boolean>) {
      state.isSettingMode = action.payload;
    },

    addSystemMessage(state, action: PayloadAction<SystemMessageType>) {
      systemMsgCounter += 1;
      state.systemMessages.push({
        id: `sys_${systemMsgCounter}`,
        type: action.payload,
        timestamp: Date.now(),
      });
    },

    reset() {
      return initialState;
    },
  },
});

export const translationActions = translationSlice.actions;
export default translationSlice.reducer;
